import { useEffect, useState } from 'react';
import type { Eu } from '../../acesso';
import { lerIntegracoes } from '../../administracao';
import type { Integracoes as DadosDasIntegracoes } from '../../administracao';
import { ErroDeArea, SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { IconeLocal, SeloDoAmbiente, hora, quando, useLeitura, useVersao } from './sistema-comum';
import './integracoes.css';

/**
 * TELA 35 — INTEGRAÇÕES E FONTES.
 *
 *   "Todo dado responde: de onde veio, quando, e se está saudável."
 *
 * - As fontes são o que realmente são hoje: lançamentos do InovaPay e a API do HOS (só leitura);
 *   o relatório do HOS só aparece enquanto a API não roda.
 * - "Última atualização" é a da última LEITURA COMPLETA E SEM ERRO (o servidor calcula), não a hora
 *   em que o valor mudou; o limite de frescor sai da configuração da busca no HOS.
 * - Cada linha pode trazer uma observação do servidor quando o número sozinho engana (vendas
 *   lançadas não são atraso de fonte; preço base e PMC não vêm pela API).
 * - Fonte sem carga fica "indisponível" com o motivo — nunca "tudo online", nunca zero.
 * - "Testar conexão" e "Importar relatório" não existem no servidor: aparecem desabilitados e a
 *   tela diz como a importação é feita hoje (pelo terminal, com o importador do repositório).
 * - Nenhuma credencial, token ou endereço interno aparece, nem mascarado.
 */

type Integracao = DadosDasIntegracoes['integracoes'][number];
type Dominio = DadosDasIntegracoes['dominios'][number];

const ROTULO_DA_INTEGRACAO: Record<Integracao['estado'], string> = {
  saudavel: 'Saudável',
  degradado: 'Degradado',
  sem_dado: 'Sem carga',
  nao_integrada: 'Não integrada',
};
const ROTULO_DO_DOMINIO: Record<Dominio['estado'], string> = {
  saudavel: 'Saudável',
  atencao: 'Atenção',
  indisponivel: 'Indisponível',
  nunca_integrado: 'Indisponível',
};
const maiuscula = (s: string) => (s ? s[0]?.toUpperCase() + s.slice(1) : s);

function IconeDaIntegracao({ nome }: { nome: string }) {
  if (nome === 'InovaPay') return <Icone nome="db" tamanho={16} traco={1.6} />;
  if (nome === 'Relatório HOS') return <Icone nome="file" tamanho={16} traco={1.6} />;
  if (nome === 'Coletor HOS') return <Icone nome="lock" tamanho={16} traco={1.6} />;
  return <IconeLocal nome="plug" tamanho={16} />;
}

function SeloDaIntegracao({ estado }: { estado: Integracao['estado'] }) {
  return (
    <span className={`ig-selo ig-selo-${estado}`}>
      {estado === 'saudavel' && <Icone nome="check" tamanho={11} traco={2.2} />}
      {estado === 'degradado' && <Icone nome="warn" tamanho={11} traco={1.9} />}
      {ROTULO_DA_INTEGRACAO[estado]}
    </span>
  );
}

export function Integracoes(_p: { eu: Eu | null }) {
  const dados = useLeitura(lerIntegracoes, 'integracoes');
  const versao = useVersao();
  const [hos, setHos] = useState(false);

  useEffect(() => {
    if (!hos) return;
    const fechar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setHos(false);
    };
    window.addEventListener('keydown', fechar);
    return () => window.removeEventListener('keydown', fechar);
  }, [hos]);

  if (dados.falha === 'sem-acesso') return <SemAcesso area="Integrações e fontes" />;
  const d = dados.d;

  return (
    <section className="ig">
      <div className="ig-faixa">
        <span className="ig-faixa-texto">
          {d
            ? `status calculados pelo servidor · atualizado às ${hora(d.atualizadoEm)}`
            : 'consultando o servidor…'}
        </span>
        <span className="ig-faixa-espaco" />
        {d && (
          <button type="button" className="ig-faixa-botao" onClick={dados.recarregar}>
            <Icone nome="cycle" tamanho={13} traco={1.7} />
            Atualizar
          </button>
        )}
        <SeloDoAmbiente v={versao.v} falhou={versao.falhou} />
      </div>

      {!d ? (
        dados.falha ? (
          <ErroDeArea oQue="as integrações" aoTentar={dados.recarregar} />
        ) : (
          <div className="ig-carregando" aria-busy="true" />
        )
      ) : (
        <>
          <div className="ig-cartoes">
            {d.integracoes.map((i) => {
              const abre = i.nome === 'Relatório HOS';
              const conteudo = (
                <>
                  <span className="ig-cartao-topo">
                    <IconeDaIntegracao nome={i.nome} />
                    <span className="ig-cartao-nome">{i.nome}</span>
                    {abre && <Icone nome="chev-r" tamanho={14} traco={1.7} />}
                  </span>
                  <SeloDaIntegracao estado={i.estado} />
                  <span className="ig-cartao-detalhe">{i.detalhe}</span>
                </>
              );
              return abre ? (
                <button
                  key={i.nome}
                  type="button"
                  className={`ig-cartao ig-cartao-${i.estado} ig-cartao-abre`}
                  onClick={() => setHos(true)}
                  aria-haspopup="dialog"
                >
                  {conteudo}
                </button>
              ) : (
                <div key={i.nome} className={`ig-cartao ig-cartao-${i.estado}`}>
                  {conteudo}
                </div>
              );
            })}
          </div>

          <div className="ig-tabela-cartao">
            <div className="ig-tabela-topo">
              <h1>Fontes por domínio</h1>
              <span>
                hora da última leitura completa · limite pela configuração da busca no HOS
              </span>
            </div>
            <div className="ig-tabela-rolo">
              <table className="ig-tabela">
                <thead>
                  <tr>
                    <th>Domínio</th>
                    <th>Fonte</th>
                    <th>Última atualização</th>
                    <th>Frescor</th>
                    <th>Status</th>
                    <th>Impacto quando falha</th>
                  </tr>
                </thead>
                <tbody>
                  {d.dominios.map((x) => (
                    <tr key={x.dominio} className={`ig-linha-${x.estado}`}>
                      <td className="ig-dominio" data-rotulo="Domínio">
                        {x.dominio}
                      </td>
                      <td data-rotulo="Fonte">
                        <span className="ig-fonte">
                          {x.fonte === 'Relatório HOS' ? (
                            <button type="button" className="ig-link" onClick={() => setHos(true)}>
                              {x.fonte}
                            </button>
                          ) : (
                            <span>{x.fonte}</span>
                          )}
                          {x.observacao && <small className="ig-obs">{x.observacao}</small>}
                        </span>
                      </td>
                      <td className="ig-quando" data-rotulo="Última atualização">
                        {x.ultimaAtualizacao ? quando(x.ultimaAtualizacao) : '—'}
                      </td>
                      <td className="ig-frescor" data-rotulo="Frescor">
                        {maiuscula(x.frescor)}
                      </td>
                      <td data-rotulo="Status">
                        <span className={`ig-selo ig-selo-${x.estado}`}>
                          {ROTULO_DO_DOMINIO[x.estado]}
                        </span>
                      </td>
                      <td className="ig-impacto" data-rotulo="Impacto quando falha">
                        {x.impacto}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ig-rodape">
              <span>{d.dominios.length} domínios</span>
              <span className="ig-rodape-legenda">
                Nunca integrado · sem carga · limite excedido — estados distintos, nunca zero
              </span>
            </div>
          </div>

          <Metricas metricas={d.metricas} />
        </>
      )}

      {hos && d && <DetalheDoHos dados={d} aoFechar={() => setHos(false)} />}
    </section>
  );
}

/* ------------------------------ definições de métricas ------------------------------ */

function Metricas({ metricas }: { metricas: DadosDasIntegracoes['metricas'] }) {
  const [aberta, setAberta] = useState<string | null>(null);
  return (
    <div className="ig-metricas">
      <div className="ig-metricas-topo">
        <h2>Definições de métricas</h2>
        <span>Nome, metodologia e fonte · a fórmula mora no servidor</span>
      </div>
      <ul className="ig-metricas-lista">
        {metricas.map((m) => {
          const ab = aberta === m.nome;
          return (
            <li key={m.nome}>
              <div className="ig-metrica">
                <div className="ig-metrica-texto">
                  <div className="ig-metrica-nome">{m.nome}</div>
                  <div className="ig-metrica-sub">
                    fonte: {m.fonte === '—' ? 'nenhuma disponível' : m.fonte}
                  </div>
                </div>
                <button
                  type="button"
                  className="ig-link"
                  aria-expanded={ab}
                  onClick={() => setAberta(ab ? null : m.nome)}
                >
                  {ab ? 'Fechar' : 'Ver definição'}
                </button>
              </div>
              {ab && <p className="ig-metrica-def">{maiuscula(m.metodologia)}.</p>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ------------------------------ detalhe do Relatório HOS ------------------------------ */

function DetalheDoHos({ dados, aoFechar }: { dados: DadosDasIntegracoes; aoFechar: () => void }) {
  const hos = dados.integracoes.find((i) => i.nome === 'Relatório HOS');
  const doHos = dados.dominios.filter(
    (x) => x.fonte === 'Relatório HOS' || x.fonte === 'PDF do HOS',
  );
  const vendas = dados.dominios.find((x) => x.dominio === 'Vendas do HOS');
  return (
    <div className="ig-veu" onClick={aoFechar}>
      <aside
        className="ig-hos"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ig-hos-titulo"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ig-hos-topo">
          <Icone nome="file" tamanho={18} traco={1.6} />
          <div className="ig-hos-titulo">
            <h2 id="ig-hos-titulo">Relatório HOS</h2>
            <span>Importação em lote · não é tempo real</span>
          </div>
          {hos && (
            <span className={`ig-selo ig-selo-grande ig-selo-${hos.estado}`}>
              {ROTULO_DA_INTEGRACAO[hos.estado].toUpperCase()}
            </span>
          )}
          <button type="button" className="ig-fechar" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={16} traco={1.7} />
          </button>
        </div>
        <div className="ig-hos-bloco">
          <div className="ig-par">
            <span>Última importação de vendas</span>
            <b>{vendas?.ultimaAtualizacao ? quando(vendas.ultimaAtualizacao) : 'nenhuma ainda'}</b>
          </div>
          <div className="ig-par">
            <span>Nova tentativa</span>
            <b>sem agendamento · a importação é manual</b>
          </div>
          <div className="ig-botoes">
            <button type="button" disabled title="O servidor ainda não oferece teste de conexão">
              Testar conexão
            </button>
            <button type="button" disabled title="O servidor ainda não recebe relatório pela tela">
              Importar relatório
            </button>
          </div>
          <div className="ig-como">
            <span className="ig-ponto" />
            <span>
              O servidor ainda não testa conexão nem recebe relatório pela tela. Hoje a importação é
              feita <b>pelo terminal</b>, com o importador do repositório (
              <code>pnpm --filter @inovapay/import import:vendas</code> e{' '}
              <code>import:produtos</code>).
            </span>
          </div>
          <p className="ig-nota">
            Nenhuma senha, token ou endereço interno aparece — nem mascarado.
          </p>
        </div>
        <div className="ig-hos-bloco">
          <div className="ig-hos-rotulo">ÚLTIMA CARGA POR DOMÍNIO</div>
          <ul className="ig-cargas">
            {doHos.map((x) => (
              <li key={x.dominio}>
                <span className="ig-cargas-quando">
                  {x.ultimaAtualizacao ? quando(x.ultimaAtualizacao) : '—'}
                </span>
                <span className="ig-cargas-nome">
                  {x.dominio} · {x.fonte}
                </span>
                <span className={`ig-cargas-estado ig-cargas-${x.estado}`}>
                  {x.estado === 'saudavel'
                    ? 'Em dia'
                    : x.estado === 'atencao'
                      ? 'Limite excedido'
                      : 'Sem carga'}
                </span>
              </li>
            ))}
          </ul>
          <p className="ig-nota">
            O servidor ainda não entrega o histórico de execuções para esta tela: aqui aparece a
            última carga concluída de cada domínio.
          </p>
        </div>
      </aside>
    </div>
  );
}
